import React from 'react';
import { Button } from 'src/components/design-system';
import styles from '../knowledge-upload-file.module.scss';
import { SUPPORTED_FILES_HELPDESK_LINK } from 'shared/constants/assets.constants';
import { useSelector } from 'src/store/use-selector';
import { getLang } from 'shared/store/selectors/lang.selector';
import { NotFound } from 'src/components/common';

type ErrorType =
  | 'unsupported-file'
  | 'unsupported-file-for-smart-format'
  | 'file-too-big'
  | 'video-too-big'
  | 'upload-failed'
  | 'conversion-failed'
  | 'file-too-small';

function FailUpload({
  error,
  onCancel,
  onRetry,
  disableRetry = false,
}: {
  error: ErrorType;
  onCancel: () => void;
  onRetry: () => void;
  disableRetry: boolean;
}) {
  const lang = useSelector(getLang('KNOWLEDGE_UPLOAD_POPUP'));

  const subtitles: { [K in ErrorType]: string } = {
    'unsupported-file': lang.UNSUPPORTED_FILE_SUBTITLE,
    'unsupported-file-for-smart-format': lang.UNSUPPORTED_SMART_FORMAT_SUBTITLE,
    'file-too-big': lang.FILE_TOO_BIG_SUBTITLE,
    'video-too-big': lang.VIDEO_TOO_BIG_SUBTITLE,
    'upload-failed': lang.FAIL_SUBTITLE,
    'conversion-failed': lang.CONVERSION_FAILED_SUBTITLE,
    'file-too-small': lang.FILE_EMPTY_SUBTITLE,
  };

  // `error` can be a raw upload failure we don't have a dedicated message for
  const subtitle = subtitles[error] ?? lang.FAIL_SUBTITLE;

  const renderSubtitle = () => {
    const linkPlaceholderMatches = subtitle.match(/\{supportedFormatsLink:.*?\}/);

    if (!linkPlaceholderMatches) {
      return subtitle;
    }

    const linkPlaceholder = linkPlaceholderMatches[0]!;
    const linkText = linkPlaceholder.slice(22, -1); // Extract display text between "{supportedFormatsLink:" and "}"

    const parts = subtitle.split(linkPlaceholder);

    return (
      <>
        {parts[0]}
        <a href={SUPPORTED_FILES_HELPDESK_LINK} target="_blank" rel="noopener noreferrer">
          {linkText}
        </a>
        {parts[1]}
      </>
    );
  };

  return (
    <>
      <div className={styles.loading}>
        {/* @ts-expect-error NotFound className prop typing issue */}
        <NotFound text="" className={styles.notFound} />
        <h6 className={styles.error}>{renderSubtitle()}</h6>
      </div>
      <div className={styles.buttons}>
        <Button className={styles.button} onClick={onCancel}>
          {lang.DISMISS}
        </Button>
        <Button className={styles.button} type="primary" onClick={onRetry} disabled={disableRetry}>
          {lang.RETRY}
        </Button>
      </div>
    </>
  );
}

export { FailUpload };
